import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { SiteHeader } from '@/components/SiteHeader';
import { Button } from '@/components/ui/button';
import { Check, Copy, ChevronRight, Globe, Code, ShoppingBag, Layout, Blocks, FileCode } from 'lucide-react';
import { toast } from 'sonner';

const PLATFORMS = [
  {
    id: 'html',
    name: 'HTML / Any Website',
    icon: FileCode,
    color: '#E44D26',
    steps: [
      { title: 'Go to your Dashboard', desc: 'Log in to whisperai.one and go to your Dashboard.' },
      { title: 'Create a Chat Bot', desc: 'Click "New Chat Bot", give it a name, and click Create.' },
      { title: 'Copy the Embed Code', desc: 'Click the Configure (gear) icon on your widget → copy the embed code from the top.' },
      { title: 'Paste Before </body>', desc: 'Open your HTML file and paste the script tag just before the closing </body> tag.' },
    ],
    code: '<!DOCTYPE html>\n<html>\n<head>\n  <title>Your Website</title>\n</head>\n<body>\n  <!-- Your website content -->\n\n  <!-- Paste this before </body> -->\n  <script src="https://whisperai.one/api/embed/widget.js?id=YOUR_WIDGET_ID"></script>\n</body>\n</html>',
    note: 'Replace YOUR_WIDGET_ID with your actual widget ID from the dashboard.',
  },
  {
    id: 'wordpress',
    name: 'WordPress',
    icon: Globe,
    color: '#21759B',
    steps: [
      { title: 'Log in to WordPress Admin', desc: 'Go to yourdomain.com/wp-admin and log in.' },
      { title: 'Go to Theme Editor', desc: 'Navigate to Appearance → Theme File Editor (or use a plugin like "Insert Headers & Footers").' },
      { title: 'Find footer.php', desc: 'In the Theme Editor, click on footer.php from the right sidebar.' },
      { title: 'Paste the Code', desc: 'Find the </body> tag and paste your Whisper.AI embed code just above it. Click "Update File".' },
    ],
    code: '<!-- Add this just before </body> in footer.php -->\n<script src="https://whisperai.one/api/embed/widget.js?id=YOUR_WIDGET_ID"></script>',
    note: 'Alternative: Install the "Insert Headers and Footers" plugin → Settings → Insert Headers and Footers → paste the code in the "Footer" section.',
  },
  {
    id: 'shopify',
    name: 'Shopify',
    icon: ShoppingBag,
    color: '#96BF48',
    steps: [
      { title: 'Go to Shopify Admin', desc: 'Log in to your Shopify admin panel.' },
      { title: 'Open Theme Editor', desc: 'Go to Online Store → Themes → click "..." → Edit Code.' },
      { title: 'Find theme.liquid', desc: 'In the Layout folder, click on theme.liquid.' },
      { title: 'Paste Before </body>', desc: 'Scroll to the bottom, find </body>, and paste the code just above it. Click Save.' },
    ],
    code: '<!-- Add this just before </body> in theme.liquid -->\n<script src="https://whisperai.one/api/embed/widget.js?id=YOUR_WIDGET_ID"></script>',
    note: 'This will add the chat widget to every page of your Shopify store.',
  },
  {
    id: 'wix',
    name: 'Wix',
    icon: Layout,
    color: '#0C6EFC',
    steps: [
      { title: 'Open Wix Dashboard', desc: 'Log in to your Wix account and open the site editor.' },
      { title: 'Go to Settings', desc: 'Click Settings (gear icon) in the left sidebar.' },
      { title: 'Custom Code', desc: 'Go to Advanced → Custom Code (or Marketing & SEO → Custom Code).' },
      { title: 'Add Code to Body - End', desc: 'Click "+ Add Code" → paste the embed code → set placement to "Body - end" → Apply to "All pages" → Save.' },
    ],
    code: '<script src="https://whisperai.one/api/embed/widget.js?id=YOUR_WIDGET_ID"></script>',
    note: 'Make sure to select "Body - end" as the placement and "All pages" to show the widget everywhere.',
  },
  {
    id: 'squarespace',
    name: 'Squarespace',
    icon: Blocks,
    color: '#000000',
    steps: [
      { title: 'Open Squarespace Settings', desc: 'Log in to your Squarespace site and go to Settings.' },
      { title: 'Code Injection', desc: 'Navigate to Advanced → Code Injection.' },
      { title: 'Paste in Footer', desc: 'Paste the embed code in the "Footer" section.' },
      { title: 'Save', desc: 'Click Save. The chat widget will appear on all pages.' },
    ],
    code: '<script src="https://whisperai.one/api/embed/widget.js?id=YOUR_WIDGET_ID"></script>',
    note: 'Code Injection is available on Business plan and above in Squarespace.',
  },
  {
    id: 'react',
    name: 'React / Next.js',
    icon: Code,
    color: '#61DAFB',
    steps: [
      { title: 'Open your index.html', desc: 'In your React project, open public/index.html (for Next.js: pages/_document.js).' },
      { title: 'Paste Before </body>', desc: 'Add the script tag just before the closing </body> tag.' },
      { title: 'Or Use useEffect', desc: 'Alternatively, add it dynamically using a useEffect hook in your root component.' },
      { title: 'Build and Deploy', desc: 'Run your build command and deploy. The widget will load on every page.' },
    ],
    code: '// Option 1: In public/index.html\n<script src="https://whisperai.one/api/embed/widget.js?id=YOUR_WIDGET_ID"></script>\n\n// Option 2: In your React component\nuseEffect(() => {\n  const script = document.createElement("script");\n  script.src = "https://whisperai.one/api/embed/widget.js?id=YOUR_WIDGET_ID";\n  document.body.appendChild(script);\n  return () => document.body.removeChild(script);\n}, []);',
    note: 'For Next.js, you can also use the next/script component with strategy="afterInteractive".',
  },
];

export default function GuidePage() {
  const [selected, setSelected] = useState('html');
  const [copiedCode, setCopiedCode] = useState(false);
  const platform = PLATFORMS.find(p => p.id === selected);

  const copyCode = () => {
    navigator.clipboard.writeText(platform.code);
    setCopiedCode(true);
    toast.success('Code copied!');
    setTimeout(() => setCopiedCode(false), 2000);
  };

  return (
    <div className="min-h-screen bg-background" data-testid="guide-page">
      <SiteHeader />

      <main className="max-w-4xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <span className="text-xs text-muted-foreground uppercase tracking-widest">Setup Guide</span>
          <h1 className="text-3xl sm:text-4xl font-bold text-foreground mt-2 mb-3" data-testid="guide-title">
            Add Whisper.AI to Your Website
          </h1>
          <p className="text-sm text-muted-foreground max-w-lg mx-auto">
            Choose your platform below and follow the step-by-step instructions. It takes less than 60 seconds.
          </p>
        </div>

        {/* Platform selector */}
        <div className="flex flex-wrap justify-center gap-2 mb-10" data-testid="platform-selector">
          {PLATFORMS.map(p => (
            <button
              key={p.id}
              onClick={() => { setSelected(p.id); setCopiedCode(false); }}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                selected === p.id
                  ? 'bg-[#10B981] text-white shadow-md'
                  : 'bg-card border border-border text-foreground hover:border-[#10B981]/40'
              }`}
              data-testid={`platform-${p.id}`}
            >
              <p.icon className="h-4 w-4" />
              {p.name}
            </button>
          ))}
        </div>

        {/* Steps */}
        {platform && (
          <div className="space-y-8" data-testid="guide-content">
            {/* Step-by-step */}
            <div className="space-y-4">
              {platform.steps.map((step, i) => (
                <div key={i} className="flex gap-4" data-testid={`step-${i}`}>
                  <div className="shrink-0">
                    <div className="w-10 h-10 rounded-xl bg-[#10B981] text-white flex items-center justify-center text-sm font-bold">
                      {i + 1}
                    </div>
                    {i < platform.steps.length - 1 && (
                      <div className="w-0.5 h-8 bg-border mx-auto mt-2" />
                    )}
                  </div>
                  <div className="pt-1">
                    <h3 className="text-sm font-semibold text-foreground">{step.title}</h3>
                    <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Code block */}
            <div className="rounded-xl overflow-hidden border border-border" data-testid="code-block">
              <div className="flex items-center justify-between px-4 py-2 bg-muted/50 border-b border-border">
                <span className="text-xs font-medium text-muted-foreground">Embed Code</span>
                <Button size="sm" variant="ghost" onClick={copyCode} className="h-7 text-xs gap-1.5" data-testid="copy-code-btn">
                  {copiedCode ? <><Check className="h-3 w-3 text-[#10B981]" /> Copied!</> : <><Copy className="h-3 w-3" /> Copy Code</>}
                </Button>
              </div>
              <pre className="p-4 bg-[#18181B] text-[#10B981] text-xs leading-relaxed overflow-x-auto">
                <code>{platform.code}</code>
              </pre>
            </div>

            {/* Note */}
            {platform.note && (
              <div className="p-4 rounded-xl bg-[#10B981]/5 border border-[#10B981]/20">
                <p className="text-xs text-foreground leading-relaxed">
                  <strong>Note:</strong> {platform.note}
                </p>
              </div>
            )}

            {/* Need widget ID? */}
            <div className="p-5 rounded-xl border border-border bg-card text-center">
              <p className="text-sm text-foreground font-medium mb-1">{"Don't have a Widget ID yet?"}</p>
              <p className="text-xs text-muted-foreground mb-4">Create your free chat bot in 60 seconds and get your embed code.</p>
              <Link to="/dashboard">
                <Button className="bg-[#10B981] hover:bg-[#059669] text-white gap-2" data-testid="go-dashboard-btn">
                  Go to Dashboard <ChevronRight className="h-4 w-4" />
                </Button>
              </Link>
            </div>

            {/* Still need help? */}
            <div className="text-center pt-4">
              <p className="text-xs text-muted-foreground">
                Still need help? Email us at <a href="mailto:support@whisperai.one" className="text-[#10B981] hover:underline">support@whisperai.one</a> or chat with our bot on the homepage.
              </p>
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-border py-6 px-6 sm:px-10">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <span className="text-xs text-muted-foreground">Whisper.AI &mdash; Powered by WPO</span>
          <Link to="/" className="text-xs text-muted-foreground hover:text-foreground">Home</Link>
        </div>
      </footer>
    </div>
  );
}
